import React from 'react'

// Split screen's View button, as Gavin drew it on 19 Oct ("Screen Shot
// 2026-10-19 at 10.25.04"): a black eye with a light ring for an iris, on a
// grey disc that is lightest towards the middle and darkens to the rim — lit
// like a button, the reverse of the radar's disc. Every number below was read
// off that mock, a 160px-radius disc, and is kept in its units.
//
// 21 Oct ("Screen Shot 2026-10-21 at 17.24.38"): the iris ring became a solid
// pupil, lit white in the middle and greying to its edge, to match the play
// bar's eye. Same disc, same almond; the pupil's radius (60) was read off that
// mock at the same scale.
//
// The disc is white at a falling opacity rather than a solid grey: over black,
// as Gavin drew it, it comes out at exactly his greys, and over a bright sky it
// still lets the picture through at the rim the way the radar above it does.
// The pupil is `currentColor`, so the button's open state (the skin's green)
// still shows on it; its shading is a black overlay so it works on any colour.

// Opacity of the white at each fraction of the radius — the mock's grey
// (0–255) over black, so 185 at 0.53 is 185 / 255 ≈ 0.73.
const DISC_STOPS: Array<[number, number]> = [
  [0, 0.78],
  [0.53, 0.73],
  [0.84, 0.5],
  [0.95, 0.37],
  [1, 0.37],
]

// The almond: corners at ±132 across, ±83 high, each lid one circular arc —
// the circle through both corners and the lid's peak has radius
// (132² + 83²) / (2 × 83) ≈ 146.5.
const ALMOND = 'M -132 0 A 146.5 146.5 0 0 1 132 0 A 146.5 146.5 0 0 1 -132 0 Z'

const PUPIL_RADIUS = 60

// The pupil's shading, as black over its colour: the mock's pupil is 255 at
// the centre and about 198 near the edge, so roughly a fifth darker there.
const PUPIL_SHADE_STOPS: Array<[number, number]> = [
  [0, 0],
  [0.5, 0.06],
  [1, 0.24],
]
let gradientIds = 0

export default function ViewEyeDisc({ size }: { size: number }) {
  // Split screen draws two of these, so each needs its own gradient id.
  const [id] = React.useState(() => `view-eye-disc-${++gradientIds}`)
  const shadeId = `${id}-pupil`
  return (
    <svg width={size} height={size} viewBox="-160 -160 320 320" aria-hidden="true" style={{ display: 'block' }}>
      <defs>
        <radialGradient id={id} cx="0" cy="0" r="160" gradientUnits="userSpaceOnUse">
          {DISC_STOPS.map(([at, opacity]) => (
            <stop key={at} offset={at} stopColor="#FFFFFF" stopOpacity={opacity} />
          ))}
        </radialGradient>
        <radialGradient id={shadeId} cx="0" cy="0" r={PUPIL_RADIUS} gradientUnits="userSpaceOnUse">
          {PUPIL_SHADE_STOPS.map(([at, opacity]) => (
            <stop key={at} offset={at} stopColor="#000000" stopOpacity={opacity} />
          ))}
        </radialGradient>
      </defs>
      <circle r="160" fill={`url(#${id})`} />
      <path d={ALMOND} fill="#000000" />
      <circle r={PUPIL_RADIUS} fill="currentColor" />
      <circle r={PUPIL_RADIUS} fill={`url(#${shadeId})`} />
    </svg>
  )
}
